import Vue from 'vue';
import { createCloudPreference } from '../cloud-preference';

const SETTINGS_KEY = 'salt-wallpaper-options-v1';
const DB_NAME = 'salt-wallpaper-v1';
const STORE = 'wallpaper';
const MAX_BYTES = 15 * 1024 * 1024;
let options: any = {};
try { options = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); } catch { /* defaults */ }
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
export const wallpaper = Vue.observable({
  url: '', name: '', error: '', busy: false,
  blur: Number.isFinite(options.blur) ? clamp(options.blur, 0, 240) : 60,
  customBlur: options.customBlur === true,
  flow: Number.isFinite(options.flow) ? clamp(options.flow, 0, 100) : 30,
});

type WallpaperPreference = { blur: number; customBlur: boolean; flow: number };
const readLocalOptions = (): WallpaperPreference => { try { const item = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); return { blur: Number.isFinite(item.blur) ? clamp(item.blur, 0, 240) : 60, customBlur: item.customBlur === true, flow: Number.isFinite(item.flow) ? clamp(item.flow, 0, 100) : 30 }; } catch { return { blur: 60, customBlur: false, flow: 30 }; } };
const validateOptions = (item: any): WallpaperPreference => { if (!item || typeof item.blur !== 'number' || !Number.isFinite(item.blur) || item.blur < 0 || item.blur > 240 || typeof item.customBlur !== 'boolean' || typeof item.flow !== 'number' || !Number.isFinite(item.flow) || item.flow < 0 || item.flow > 100) throw new Error('云端背景设置无效'); return { blur: item.blur, customBlur: item.customBlur, flow: item.flow }; };
export const wallpaperPreference = createCloudPreference<WallpaperPreference>('wallpaper', readLocalOptions, value => { wallpaper.blur = value.blur; wallpaper.customBlur = value.customBlur; wallpaper.flow = value.flow; }, validateOptions);
function persistOptions() {
  if (wallpaperPreference.state.storage === 'cloud') { wallpaperPreference.changed({ blur: wallpaper.blur, customBlur: wallpaper.customBlur, flow: wallpaper.flow }); return; }
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify({blur: wallpaper.blur, customBlur: wallpaper.customBlur, flow: wallpaper.flow})); wallpaper.error = ''; }
  catch { wallpaper.error = '无法保存外观设置，请检查浏览器存储空间。'; }
}
export function setWallpaperBlur(value: number) { wallpaper.blur = clamp(value, 0, 240); wallpaper.customBlur = true; persistOptions(); }
export function resetWallpaperBlur() { wallpaper.customBlur = false; wallpaper.blur = 60; persistOptions(); }
export function setWallpaperFlow(value: number) { wallpaper.flow = clamp(value, 0, 100); persistOptions(); }

function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('存储被其他页面阻止'));
  });
}
async function readImage(): Promise<{ blob: Blob; name: string } | undefined> {
  const database = await db();
  try { return await new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readonly'); const request = tx.objectStore(STORE).get('current');
    request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
  }); } finally { database.close(); }
}
async function writeImage(value?: { blob: Blob; name: string }) {
  const database = await db();
  try { await new Promise<void>((resolve, reject) => {
    const tx = database.transaction(STORE, 'readwrite');
    if (value) tx.objectStore(STORE).put(value, 'current'); else tx.objectStore(STORE).delete('current');
    tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
  }); } finally { database.close(); }
}
function showImage(value?: { blob: Blob; name: string }) {
  const old = wallpaper.url;
  wallpaper.url = value?.blob ? URL.createObjectURL(value.blob) : '';
  wallpaper.name = value?.name || '';
  if (old) URL.revokeObjectURL(old);
}
let generation = 0;
export async function loadWallpaper() {
  const current = generation;
  try { const value = await readImage(); if (generation === current) showImage(value); }
  catch { wallpaper.error = '无法读取保存的壁纸，请检查浏览器存储。'; }
}
export async function chooseWallpaper(file: File) {
  if (!file.type.startsWith('image/') || file.size > MAX_BYTES) { wallpaper.error = '请选择不超过 15 MB 的图片。'; return; }
  wallpaper.busy = true; wallpaper.error = '';
  const objectUrl = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => { const image = new Image(); image.onload = () => resolve(); image.onerror = () => reject(new Error('图片无法读取')); image.src = objectUrl; });
    const value = {blob: file, name: file.name};
    await writeImage(value); generation++; showImage(value);
  } catch { wallpaper.error = '壁纸无法读取或保存，请尝试其他图片。'; }
  finally { URL.revokeObjectURL(objectUrl); wallpaper.busy = false; }
}
export async function clearWallpaper() {
  wallpaper.busy = true; wallpaper.error = '';
  try { await writeImage(); generation++; showImage(); }
  catch { wallpaper.error = '无法移除壁纸，请检查浏览器存储。'; }
  finally { wallpaper.busy = false; }
}
void loadWallpaper();
